// src/types.ts — Core data model

export interface Task {
  id: string;
  label: string;
  emoji: string;
  required: boolean;
}

export interface DayLog {
  date: string; // YYYY-MM-DD
  tasks: Record<string, boolean>;
  photo?: string; // base64 data URI
  notes?: string;
  completedAt?: string; // ISO timestamp when all tasks checked
}

export interface Challenge {
  id: string;
  name: string;
  description: string;
  durationDays: number;
  tasks: Task[];
  isCustom: boolean;
  color?: string;
}

export interface Attempt {
  id: string;
  challengeId: string;
  startDate: string; // YYYY-MM-DD
  currentDay: number;
  status: 'active' | 'failed' | 'completed';
  failedOnDay?: number;
  failedDate?: string;
  completedDate?: string;
  logs: Record<string, DayLog>; // keyed by YYYY-MM-DD
}

export interface AppState {
  challenges: Challenge[];
  attempts: Attempt[];
  activeAttemptId: string | null;
  onboarded: boolean;
  premium: boolean;
  settings: {
    theme: 'dark' | 'light';
    reminderTime?: string;
    userName?: string;
  };
}

export const DEFAULT_75HARD_TASKS: Task[] = [
  { id: 'workout1', label: 'Workout 1 (45 min)', emoji: '💪', required: true },
  { id: 'workout2', label: 'Workout 2 — outdoor (45 min)', emoji: '🏃', required: true },
  { id: 'diet', label: 'Follow your diet', emoji: '🥗', required: true },
  { id: 'water', label: 'Drink 1 gallon of water', emoji: '💧', required: true },
  { id: 'reading', label: 'Read 10 pages', emoji: '📖', required: true },
  { id: 'photo', label: 'Take a progress photo', emoji: '📸', required: true },
];

export const DEFAULT_75HARD: Challenge = {
  id: '75hard-official',
  name: '75 Hard',
  description: 'The original mental toughness program. Two workouts, strict diet, one gallon of water, 10 pages of reading, and a progress photo — every single day for 75 days. No substitutions. No excuses.',
  durationDays: 75,
  tasks: DEFAULT_75HARD_TASKS,
  isCustom: false,
  color: '#f97316',
};

export const PRESET_CHALLENGES: Challenge[] = [
  DEFAULT_75HARD,
  {
    id: '75medium',
    name: '75 Medium',
    description: 'A balanced approach. One workout per day, follow a flexible diet, 3L of water, 10 pages of reading, and daily journaling. Same duration, less extreme.',
    durationDays: 75,
    tasks: [
      { id: 'workout1', label: 'Workout (45 min)', emoji: '💪', required: true },
      { id: 'diet', label: 'Follow your diet plan', emoji: '🥗', required: true },
      { id: 'water', label: 'Drink 3L of water', emoji: '💧', required: true },
      { id: 'reading', label: 'Read 10 pages', emoji: '📖', required: true },
      { id: 'journal', label: 'Journal for 10 minutes', emoji: '📝', required: true },
    ],
    isCustom: false,
    color: '#8b5cf6',
  },
  {
    id: '75soft',
    name: '75 Soft',
    description: 'The beginner-friendly version. One workout, follow a diet with one cheat meal per week, 3L of water, and 10 pages of reading.',
    durationDays: 75,
    tasks: [
      { id: 'workout1', label: 'Workout (45 min)', emoji: '💪', required: true },
      { id: 'diet', label: 'Follow diet (1 flex meal/week)', emoji: '🥗', required: true },
      { id: 'water', label: 'Drink 3L of water', emoji: '💧', required: true },
      { id: 'reading', label: 'Read 10 pages', emoji: '📖', required: true },
    ],
    isCustom: false,
    color: '#06b6d4',
  },
  {
    id: '30day-kickstart',
    name: '30 Day Kickstart',
    description: 'A 30-day starter challenge. One workout, nutrition plan, water target, and daily journaling. Perfect for building the habit.',
    durationDays: 30,
    tasks: [
      { id: 'workout1', label: 'Workout (30 min)', emoji: '💪', required: true },
      { id: 'diet', label: 'Follow nutrition plan', emoji: '🥗', required: true },
      { id: 'water', label: 'Drink 2.5L of water', emoji: '💧', required: true },
      { id: 'journal', label: 'Journal for 5 minutes', emoji: '📝', required: true },
    ],
    isCustom: false,
    color: '#22c55e',
  },
];

export const MOTIVATIONAL_QUOTES = [
  "The pain you feel today will be the strength you feel tomorrow.",
  "Don't stop when you're tired. Stop when you're done.",
  "The only bad workout is the one that didn't happen.",
  "Discipline is choosing between what you want now and what you want most.",
  "You don't have to be extreme, just consistent.",
  "The harder you work, the luckier you get.",
  "Small daily improvements over time lead to stunning results.",
  "Your body can stand almost anything. It's your mind you have to convince.",
  "Success isn't given. It's earned.",
  "The best time to start was yesterday. The next best time is now.",
  "It's not about motivation. It's about discipline.",
  "75 days is nothing. The habits last forever.",
  "You're not doing this to impress anyone. You're doing this for you.",
  "Every day you complete is proof you can do hard things.",
  "Comfort is the enemy of progress.",
];
